import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { clearAuth, getUser, setUser } from '@/lib/auth';
import { usePermissoes, TODAS_PAGINAS } from '@/lib/permissoes';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { toast } from 'sonner';
import {
  LayoutDashboard,
  Users,
  Banknote,
  TrendingUp,
  FileClock,
  Settings,
  LogOut,
  Menu,
  X,
  GraduationCap,
  AlertCircle,
  Receipt,
  FileText,
  Upload,
  MessageCircle,
  Bell,
  PieChart,
  BookOpen,
  Briefcase,
  Clock,
  School,
  Link2,
  Calculator,
  ClipboardList,
  CreditCard,
  CalendarDays,
  Key,
  Eye,
  EyeOff,
  DollarSign,
  Building2,
  ChevronDown,
  ChevronRight,
  Brain,
  BarChart3,
  Shield,
  UserCog,
  FolderOpen,
  Lock,
  Sparkles,
  Cake,
  Megaphone,
  ExternalLink,
  Trophy,
  Swords,
  FileEdit,
  Bus,
  UserCheck,
  AlertTriangle,
  Play,
  Send,
  Target,
  Zap,
  MousePointer,
  MessageSquare,
  Calendar,
  Star,
  Gamepad2
} from 'lucide-react';

// Mapeamento de ícones para cada página
const PAGE_ICONS = {
  dnapay_dashboard: DollarSign,
  dnapay_scheduler: Clock,
  dnapay_renegociacao: CreditCard,
  dnapay_mensageria: MessageCircle,
  dashboard: LayoutDashboard,
  collection: PieChart,
  geracao_cobranca: CreditCard,
  integracao_bancaria: Building2,
  folha_pagamento: DollarSign,
  titles: Banknote,
  contas_bancarias: Building2,
  financial_entries: Receipt,
  dre: TrendingUp,
  rateio: Calculator,
  bases_rateio: Users,
  expenses: Receipt,
  financial_plans: FileText,
  bank: TrendingUp,
  defaulters: AlertCircle,
  dnapay_gerar_titulos: Zap,
  dnapay_cobrancas_automaticas: Bell,
  students: Users,
  boletim: ClipboardList,
  class_schedule: CalendarDays,
  teacher_portal: School,
  teacher_assignments: Link2,
  class_groups: GraduationCap,
  courses: BookOpen,
  curriculums: BookOpen,
  carteirinhas: CreditCard,
  agenda: CalendarDays,
  documentos: FileEdit,
  dna_ia: Brain,
  assistente_ia: Sparkles,
  tabuada: Calculator,
  quiz_milionario: Trophy,
  interclasse: Swords,
  torneio_profissional: Trophy,
  aniversariantes: Cake,
  canva: ExternalLink,
  campanhas: Megaphone,
  templates: FileText,
  mural_comunicados: MessageCircle,
  employees: Briefcase,
  time_records: Clock,
  usuarios: UserCog,
  communication: MessageCircle,
  reminders: Bell,
  alert_settings: Bell,
  import_export: Upload,
  logs: FileClock,
  settings: Settings,
  relatorio_consolidado: BarChart3,
  analytics: TrendingUp,
  relatorios: FileText,
  import_titles: Upload,
  // DNA GO
  dnago_painel: Play,
  dnago_portaria: Shield,
  dnago_dashboard: BarChart3,
  dnago_autorizados: UserCheck,
  dnago_retiradas: ClipboardList,
  dnago_vans: Bus,
  dnago_ocorrencias: AlertTriangle,
  // DNA CONNECT
  dnaconnect_admin: Users,
  aulas_extras_admin: Sparkles,
  // DNA GROWTH ENGINE
  growth_dashboard: Zap,
  growth_crm: Target,
  growth_social: MousePointer,
  growth_atendimento: MessageSquare,
  growth_ads: Megaphone,
  growth_funnel: Target,
  growth_whatsapp: MessageSquare,
  growth_analytics: TrendingUp,
  growth_reputation: Star,
  // CALENDÁRIO ESCOLAR
  calendario: Calendar,
  // FISCAL
  fiscal_dashboard: Receipt,
  fiscal_emissao: Send,
  fiscal_notas: FileText,
  fiscal_config: Settings,
  fiscal_relatorios: BarChart3,
  fiscal_integracao: Building2,
  fiscal_logs: ClipboardList,
  fiscal_erros: AlertTriangle,
  // TRIBUTARIO
  tributario_dashboard: Calculator,
  tributario_config: Settings,
  tributario_apuracao: Play,
  tributario_memoria: FileText,
  tributario_simulacoes: TrendingUp,
  tributario_relatorios: BarChart3,
  tributario_logs: ClipboardList,
  // EXECUTIVO
  executivo_dashboard: LayoutDashboard,
  executivo_dre: FileText,
  executivo_segmentos: PieChart,
  executivo_forecast: TrendingUp,
  // ESTRATÉGICO
  estrategico_expansao: Building2,
  estrategico_custos: TrendingUp,
};

// Estrutura das categorias (sem items fixos - serão filtrados dinamicamente)
const CATEGORIAS_CONFIG = [
  {
    id: 'dnacore',
    modulo: 'dnacore',
    label: 'DNACore',
    icon: Brain,
    color: 'text-purple-600',
    bgColor: 'bg-purple-50',
    isTopLevel: true,
    path: '/dnacore-v2',
  },
  {
    id: 'central_notas',
    modulo: 'dnacore',
    label: 'Central de Notas',
    icon: BookOpen,
    color: 'text-purple-600',
    bgColor: 'bg-purple-50',
    isTopLevel: true,
    path: '/central-notas',
    subItems: [
      { id: 'central_notas', label: 'Visão Geral', path: '/central-notas' },
      { id: 'central_notas_pendencias', label: 'IA Pendências', path: '/central-notas/pendencias', badge: 'IA' },
      { id: 'semana_avaliacoes', label: 'Semana de Avaliações', path: '/semana-avaliacoes', badge: 'IA' }
    ]
  },
  {
    id: 'quiz_live',
    modulo: 'quiz_live',
    label: 'Quiz Live',
    icon: Gamepad2,
    color: 'text-green-600',
    bgColor: 'bg-green-50',
    isTopLevel: true,
    path: '/quiz-live',
  },
  {
    id: 'financeiro',
    modulo: 'financeiro',
    label: 'DNA Pay',
    icon: DollarSign,
    color: 'text-purple-600',
    bgColor: 'bg-purple-50',
  },
  {
    id: 'pedagogico',
    modulo: 'pedagogico',
    label: 'Pedagógico',
    icon: GraduationCap,
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
  },
  {
    id: 'dna_ia',
    modulo: 'dna_ia',
    label: 'DNA.IA',
    icon: Brain,
    color: 'text-purple-600',
    bgColor: 'bg-purple-50',
  },
  {
    id: 'marketing',
    modulo: 'marketing',
    label: 'Marketing',
    icon: Megaphone,
    color: 'text-pink-600',
    bgColor: 'bg-pink-50',
  },
  {
    id: 'administrativo',
    modulo: 'administrativo',
    label: 'Administrativo',
    icon: Briefcase,
    color: 'text-orange-600',
    bgColor: 'bg-orange-50',
  },
  {
    id: 'relatorios',
    modulo: 'relatorios',
    label: 'Relatórios',
    icon: BarChart3,
    color: 'text-slate-600',
    bgColor: 'bg-slate-50',
  },
  {
    id: 'dnago',
    modulo: 'dnago',
    label: 'DNA GO',
    icon: Shield,
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-50',
  },
  {
    id: 'dnaconnect',
    modulo: 'dnaconnect',
    label: 'DNA CONNECT',
    icon: Users,
    color: 'text-purple-600',
    bgColor: 'bg-purple-50',
  },
  {
    id: 'growth',
    modulo: 'growth',
    label: 'Growth Engine',
    icon: Zap,
    color: 'text-amber-500',
    bgColor: 'bg-amber-50',
  },
  {
    id: 'calendario',
    modulo: 'calendario',
    label: 'Calendário Escolar',
    icon: Calendar,
    color: 'text-purple-600',
    bgColor: 'bg-purple-50',
  },
  {
    id: 'fiscal',
    modulo: 'fiscal',
    label: 'Fiscal NFS-e',
    icon: Receipt,
    color: 'text-amber-600',
    bgColor: 'bg-amber-50',
  },
  {
    id: 'tributario',
    modulo: 'tributario',
    label: 'Tributário',
    icon: Calculator,
    color: 'text-purple-600',
    bgColor: 'bg-purple-50',
  },
  {
    id: 'executivo',
    modulo: 'executivo',
    label: 'Dashboard Executivo',
    icon: LayoutDashboard,
    color: 'text-green-600',
    bgColor: 'bg-green-50',
  },
  {
    id: 'estrategico',
    modulo: 'estrategico',
    label: 'Estratégico',
    icon: TrendingUp,
    color: 'text-indigo-600',
    bgColor: 'bg-indigo-50',
  },
];

export default function Layout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const user = getUser();
  const { temAcessoCategoria, temAcessoPagina, getPaginasPermitidas, loading: loadingPermissoes } = usePermissoes();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState([]);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [changingPassword, setChangingPassword] = useState(false);
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });

  // Gerar menu dinamicamente baseado nas permissões granulares
  const menuComPermissoes = CATEGORIAS_CONFIG
    .filter(categoria => temAcessoCategoria(categoria.modulo))
    .map(categoria => {
      const paginasPermitidas = getPaginasPermitidas(categoria.modulo);
      
      // Filtrar subItems baseado nas permissões (para itens isTopLevel com subItems)
      let filteredSubItems = categoria.subItems;
      if (categoria.subItems && categoria.subItems.length > 0) {
        filteredSubItems = categoria.subItems.filter(subItem => temAcessoPagina(subItem.id));
      }
      
      return {
        ...categoria,
        subItems: filteredSubItems,
        items: paginasPermitidas.map(pagina => ({
          id: pagina.id,
          icon: PAGE_ICONS[pagina.id] || FileText,
          label: pagina.label,
          path: pagina.path,
        }))
      };
    })
    .filter(categoria => {
      // Manter se é isTopLevel COM subItems permitidos OU se tem items regulares
      if (categoria.isTopLevel && categoria.subItems && categoria.subItems.length > 0) {
        return true;
      }
      if (categoria.isTopLevel && (!categoria.subItems || categoria.subItems.length === 0)) {
        // isTopLevel sem subItems - verificar se tem permissão para a página principal
        const pageId = categoria.id;
        return temAcessoPagina(pageId);
      }
      return categoria.items.length > 0;
    });

  // Expandir categoria ativa automaticamente
  useEffect(() => {
    for (const category of menuComPermissoes) {
      // Ignorar categorias de topo nível (não têm subitens)
      if (category.isTopLevel) continue;
      
      if (category.items?.some(item => item.path === location.pathname)) {
        if (!expandedCategories.includes(category.id)) {
          setExpandedCategories(prev => [...prev, category.id]);
        }
        break;
      }
    }
  }, [location.pathname, menuComPermissoes]);

  const handleLogout = () => {
    clearAuth();
    navigate('/login');
  };

  const handleChangePassword = async () => {
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('As senhas não coincidem');
      return;
    }
    if (passwordForm.new_password.length < 6) {
      toast.error('A nova senha deve ter pelo menos 6 caracteres');
      return;
    }

    setChangingPassword(true);
    try {
      await api.post('/auth/change-password', {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password
      });
      toast.success('Senha alterada com sucesso!');
      setPasswordDialogOpen(false);
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao alterar senha');
    } finally {
      setChangingPassword(false);
    }
  };

  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => 
      prev.includes(categoryId) 
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  return (
    <div className="flex h-screen bg-background notranslate" translate="no">
      {/* Sidebar */}
      <aside
        className={`${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0 fixed lg:static inset-y-0 left-0 z-50 w-[280px] sm:w-72 bg-card border-r border-border transition-transform duration-300 ease-out flex flex-col safe-area-inset notranslate`}
      >
        {/* Header com logo */}
        <div className="p-3 sm:p-4 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center overflow-hidden bg-white flex-shrink-0">
              <img src="https://customer-assets.emergentagent.com/job_gestao-escolar-9/artifacts/kq81pgzg_NOVO%20DNA%20-%20logo.png" alt="DNACore" className="w-full h-full object-contain" />
            </div>
            <div className="min-w-0">
              <h2 className="font-bold text-base sm:text-lg text-purple-900 truncate">DNACore</h2>
              <p className="text-[10px] sm:text-xs text-muted-foreground truncate">#muitasmentes uma missão</p>
            </div>
          </div>
          {/* Botão fechar em mobile */}
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden flex-shrink-0 h-8 w-8"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="w-4 h-4" />
          </Button>
        </div>

        <nav className="flex-1 p-2 sm:p-3 space-y-1 overflow-y-auto scroll-smooth-y">
          {loadingPermissoes ? (
            <div className="flex items-center justify-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
            </div>
          ) : menuComPermissoes.length === 0 ? (
            <div className="text-center py-4 text-muted-foreground">
              <Lock className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">Sem acesso a módulos</p>
              <p className="text-xs">Contate o administrador</p>
            </div>
          ) : (
            menuComPermissoes.map((category) => {
              const CategoryIcon = category.icon;
              const isExpanded = expandedCategories.includes(category.id);
              const hasActiveItem = category.items?.some(item => item.path === location.pathname);
              
              // Se é item de topo nível (link direto), renderiza como link simples ou com submenu
              if (category.isTopLevel) {
                const isActive = location.pathname === category.path || location.pathname.startsWith(category.path + '/');
                const hasSubItems = category.subItems && category.subItems.length > 0;
                
                // Se tem subitems, renderiza com submenu expansível
                if (hasSubItems) {
                  const isSubExpanded = expandedCategories.includes(category.id);
                  const hasActiveSubItem = category.subItems.some(item => location.pathname === item.path);
                  
                  return (
                    <Collapsible
                      key={category.id}
                      open={isSubExpanded || hasActiveSubItem}
                      onOpenChange={() => toggleCategory(category.id)}
                    >
                      <CollapsibleTrigger asChild>
                        <Button
                          variant="ghost"
                          className={`w-full justify-between ${(isActive || hasActiveSubItem) ? category.bgColor : 'hover:bg-accent'}`}
                          data-testid={`menu-${category.id}`}
                        >
                          <div className="flex items-center gap-2">
                            <div className={`p-1.5 rounded-md ${category.bgColor}`}>
                              <CategoryIcon className={`w-4 h-4 ${category.color}`} />
                            </div>
                            <span className={`font-semibold ${(isActive || hasActiveSubItem) ? category.color : ''}`}>
                              {category.label}
                            </span>
                          </div>
                          {isSubExpanded || hasActiveSubItem ? (
                            <ChevronDown className="w-4 h-4 text-muted-foreground" />
                          ) : (
                            <ChevronRight className="w-4 h-4 text-muted-foreground" />
                          )}
                        </Button>
                      </CollapsibleTrigger>
                      <CollapsibleContent className="pl-4 space-y-0.5 mt-1">
                        {category.subItems.map((subItem) => {
                          const isSubActive = location.pathname === subItem.path;
                          return (
                            <Link key={subItem.path} to={subItem.path} onClick={() => setSidebarOpen(false)}>
                              <Button
                                variant={isSubActive ? 'default' : 'ghost'}
                                size="sm"
                                className={`w-full justify-start text-sm ${
                                  isSubActive 
                                    ? 'bg-gradient-to-r from-purple-600 to-purple-700 text-white' 
                                    : 'hover:bg-accent text-muted-foreground hover:text-foreground'
                                }`}
                                data-testid={`menu-${subItem.id}`}
                              >
                                <span className="truncate">{subItem.label}</span>
                                {subItem.badge && (
                                  <span className="ml-auto px-1.5 py-0.5 text-[10px] font-bold bg-gradient-to-r from-purple-500 to-green-500 text-white rounded-md">
                                    {subItem.badge}
                                  </span>
                                )}
                              </Button>
                            </Link>
                          );
                        })}
                      </CollapsibleContent>
                    </Collapsible>
                  );
                }
                
                // Sem subitems, renderiza como link simples
                return (
                  <Link key={category.id} to={category.path} onClick={() => setSidebarOpen(false)}>
                    <Button
                      variant={isActive ? 'default' : 'ghost'}
                      className={`w-full justify-start ${isActive ? 'bg-gradient-to-r from-purple-600 to-purple-700 text-white' : category.bgColor + ' hover:shadow-md'}`}
                      data-testid={`menu-${category.id}`}
                    >
                      <div className="flex items-center gap-2">
                        <div className={`p-1.5 rounded-md ${isActive ? 'bg-white/20' : category.bgColor}`}>
                          <CategoryIcon className={`w-4 h-4 ${isActive ? 'text-white' : category.color}`} />
                        </div>
                        <span className={`font-semibold ${isActive ? 'text-white' : category.color}`}>
                          {category.label}
                        </span>
                      </div>
                    </Button>
                  </Link>
                );
              }
              
              return (
                <Collapsible
                  key={category.id}
                  open={isExpanded}
                  onOpenChange={() => toggleCategory(category.id)}
                >
                  <CollapsibleTrigger asChild>
                    <Button
                      variant="ghost"
                      className={`w-full justify-between ${hasActiveItem ? category.bgColor : 'hover:bg-accent'}`}
                      data-testid={`menu-category-${category.id}`}
                    >
                      <div className="flex items-center gap-2">
                        <div className={`p-1.5 rounded-md ${category.bgColor}`}>
                          <CategoryIcon className={`w-4 h-4 ${category.color}`} />
                        </div>
                        <span className={`font-medium ${hasActiveItem ? category.color : ''}`}>
                          {category.label}
                        </span>
                      </div>
                      {isExpanded ? (
                        <ChevronDown className="w-4 h-4 text-muted-foreground" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-muted-foreground" />
                      )}
                    </Button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="pl-4 space-y-0.5 mt-1">
                    {category.items?.map((item) => {
                      const Icon = item.icon;
                      const isActive = location.pathname === item.path;
                      return (
                        <Link key={item.path} to={item.path} onClick={() => setSidebarOpen(false)}>
                          <Button
                            variant={isActive ? 'default' : 'ghost'}
                            size="sm"
                            className={`w-full justify-start text-sm ${
                              isActive 
                                ? 'bg-primary text-primary-foreground' 
                                : 'hover:bg-accent text-muted-foreground hover:text-foreground'
                            }`}
                            data-testid={`menu-${item.id}`}
                          >
                            <Icon className="w-4 h-4 mr-2" />
                            {item.label}
                          </Button>
                        </Link>
                      );
                    })}
                  </CollapsibleContent>
                </Collapsible>
              );
            })
          )}
        </nav>

        <div className="p-2 sm:p-3 border-t border-border">
          <div className="mb-2 sm:mb-3 p-2 sm:p-3 bg-muted/50 rounded-md">
            <p className="text-xs sm:text-sm font-medium truncate">{user?.name}</p>
            <p className="text-[10px] sm:text-xs text-muted-foreground truncate">{user?.email}</p>
            <span className="inline-block mt-1 sm:mt-2 px-2 py-0.5 text-[10px] sm:text-xs font-medium bg-primary/10 text-primary rounded-full">
              {user?.role}
            </span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start mb-1 sm:mb-2 h-9 sm:h-10 text-xs sm:text-sm"
            onClick={() => setPasswordDialogOpen(true)}
            data-testid="change-password-button"
          >
            <Key className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-2" />
            Alterar Senha
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="w-full h-9 sm:h-10 text-xs sm:text-sm"
            onClick={handleLogout}
            data-testid="logout-button"
          >
            <LogOut className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-2" />
            Sair
          </Button>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* Top bar - responsivo */}
        <header className="sticky top-0 z-40 backdrop-blur-xl bg-background/80 border-b border-border/40 safe-area-inset">
          <div className="flex items-center justify-between px-3 sm:px-6 py-2 sm:py-3">
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden h-9 w-9 sm:h-10 sm:w-10 touch-target"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              data-testid="mobile-menu-toggle"
            >
              <Menu className="w-5 h-5" />
            </Button>
            <div className="flex-1 lg:flex-none" />
            <div className="flex items-center gap-2">
              <span className="text-xs sm:text-sm text-muted-foreground hidden sm:inline truncate max-w-[200px]">
                Bem-vindo, {user?.name}
              </span>
            </div>
          </div>
        </header>

        {/* Content - com padding responsivo */}
        <main className="flex-1 overflow-y-auto scroll-smooth-y">
          <div className="page-enter">
            {children}
          </div>
        </main>
      </div>

      {/* Overlay for mobile sidebar - com blur */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm lg:hidden transition-opacity"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Modal de Alteração de Senha */}
      <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-primary" />
              Alterar Senha
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="current_password">Senha Atual</Label>
              <div className="relative">
                <Input
                  id="current_password"
                  type={showPasswords.current ? "text" : "password"}
                  value={passwordForm.current_password}
                  onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
                  placeholder="Digite sua senha atual"
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPasswords({ ...showPasswords, current: !showPasswords.current })}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPasswords.current ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="new_password">Nova Senha</Label>
              <div className="relative">
                <Input
                  id="new_password"
                  type={showPasswords.new ? "text" : "password"}
                  value={passwordForm.new_password}
                  onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                  placeholder="Digite a nova senha (mín. 6 caracteres)"
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPasswords.new ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="confirm_password">Confirmar Nova Senha</Label>
              <div className="relative">
                <Input
                  id="confirm_password"
                  type={showPasswords.confirm ? "text" : "password"}
                  value={passwordForm.confirm_password}
                  onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
                  placeholder="Confirme a nova senha"
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPasswords.confirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setPasswordDialogOpen(false)} disabled={changingPassword}>
              Cancelar
            </Button>
            <Button onClick={handleChangePassword} disabled={changingPassword}>
              {changingPassword ? 'Alterando...' : 'Alterar Senha'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
